import type { ReactNode } from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type SymbolVariant =
	| 'neutral'
	| 'success'
	| 'error'
	| 'warning'
	| 'information'
	| 'brand';

const DefaultSymbolVariant: SymbolVariant = 'neutral' as const;

export type SymbolSize = Extract<Size, 'md' | 'lg' | 'xl'>;

const DefaultSymbolSize: SymbolSize = 'md' as const;

/**
 *	The symbol is inert: it reports a status, it does not offer an action. So it
 *	takes neither the `on*` handlers nor the `data-*` index signature every other
 *	entity accepts, and spreads nothing onto its element. A symbol that needs to
 *	be pressed is an icon button with a symbol in it, not this.
 */
interface SymbolProperties {
	variant?: SymbolVariant;
	size?: SymbolSize;
	rounded?: boolean;

	icon: ReactNode;
}

export function Symbol({
	variant = DefaultSymbolVariant,
	size = DefaultSymbolSize,
	rounded = false,

	icon,
}: SymbolProperties): ReactNode {
	const className: string = cn(
		css.symbol,
		css[variant],
		css[size],
		rounded && css.rounded
	);

	return (
		<div
			data-variant={variant}
			data-size={size}
			data-rounded={rounded}
			className={className}>
			{icon}
		</div>
	);
}
